/**
 * Website / retail price grids used as the SP Quote "green" (ask) band.
 *
 * Screen / DTF sheets include garment+print (example garment noted in `cogs`).
 * The example garment is swapped out for the quoted garment:
 *   ask = sheet price − sheet cogs + actual garment COGS
 * Embroidery sheet is decoration-only — callers add actual garment COGS.
 */

export const RETAIL_BASELINES = {
  screen: {
    label: 'Bella+Canvas 3001',
    cogs: 4.2,
    // minQty → [1 loc, 2 loc, 3 loc] $/pc including garment+print
    tiers: [
      { minQty: 250, prices: [9.0, 11.25, 13.5] },
      { minQty: 100, prices: [10.0, 12.25, 14.5] },
      { minQty: 48, prices: [12.0, 14.25, 16.5] },
      { minQty: 24, prices: [14.0, 16.25, 18.5] },
    ],
  },
  dtf: {
    label: 'Gildan 980',
    cogs: 2.99,
    setupFee: 20,
    // minQty → [1 side, 2 sides] — 100+ is "get in touch"
    tiers: [
      { minQty: 50, prices: [14.0, 20.0] },
      { minQty: 13, prices: [16.0, 22.0] },
      { minQty: 1, prices: [18.0, 24.0] },
    ],
    maxQty: 99,
  },
};

/** Website embroidery stitch dropdown options (decoration only, no garment). */
export const EMBROIDERY_STITCH_TIERS = [
  { value: 'under5k', label: 'Less than 5,000 stitches', stitches: 4999 },
  { value: '5k10k', label: '5,000 – 10,000 stitches', stitches: 10000 },
  { value: '11k', label: '11,000 stitches', stitches: 11000 },
  { value: '12k', label: '12,000 stitches', stitches: 12000 },
  { value: '13k', label: '13,000 stitches', stitches: 13000 },
  { value: '14k', label: '14,000 stitches', stitches: 14000 },
  { value: '15k', label: '15,000 stitches', stitches: 15000 },
  { value: '16k', label: '16,000 stitches', stitches: 16000 },
  { value: '18k', label: '18,000 stitches', stitches: 18000 },
  { value: '20k', label: '20,000 stitches', stitches: 20000 },
];

export const EMBROIDERY_DIGITIZING_PER_LOCATION = 45;
export const EMBROIDERY_SMALL_QTY_FEE = 45; // fewer than 8 pcs
export const EMBROIDERY_SMALL_QTY_THRESHOLD = 8;

/**
 * Embroidery decoration $/pc from website grid.
 * Qty columns on sheet: ≤24 | 25–150 | 150+ (get in touch).
 */
const EMBROIDERY_DECORATION = {
  label: 'General embroidery',
  maxQty: 150,
  // base by stitch band, then +addon per 1k over 10k
  under5k: { small: 11.5, mid: 8.5 },
  mid10k: { small: 16.0, mid: 12.0 },
  addonPer1k: { small: 1.0, mid: 0.9 },
};

function clampLocations(locationCount, maxLocs) {
  const n = Math.max(0, Math.floor(Number(locationCount) || 0));
  if (n < 1) return 0;
  return Math.min(n, maxLocs);
}

function lookupTier(tiers, quantity) {
  const qty = Number(quantity) || 0;
  for (const tier of tiers) {
    if (qty >= tier.minQty) return tier;
  }
  return null;
}

function embroideryQtyBucket(quantity) {
  const qty = Number(quantity) || 0;
  if (qty <= 0) return null;
  if (qty > EMBROIDERY_DECORATION.maxQty) return 'unavailable';
  if (qty <= 24) return 'small';
  return 'mid'; // 25–150
}

function stitchTierMeta(value) {
  return EMBROIDERY_STITCH_TIERS.find((t) => t.value === value) || EMBROIDERY_STITCH_TIERS[0];
}

/** Decoration-only $/pc for one location at a stitch tier. */
export function embroideryDecorationPerLocation(quantity, stitchTierValue) {
  const bucket = embroideryQtyBucket(quantity);
  if (!bucket || bucket === 'unavailable') return null;

  const meta = stitchTierMeta(stitchTierValue);
  const stitches = meta.stitches || 0;
  const base =
    stitches < 5000
      ? EMBROIDERY_DECORATION.under5k[bucket]
      : EMBROIDERY_DECORATION.mid10k[bucket];
  const extraK = stitches > 10000 ? Math.ceil((stitches - 10000) / 1000) : 0;
  const addon = extraK * EMBROIDERY_DECORATION.addonPer1k[bucket];
  return base + addon;
}

export function embroideryDigitizingFee(locationCount, quantity) {
  const locs = Math.max(0, Number(locationCount) || 0);
  const digitizing = locs * EMBROIDERY_DIGITIZING_PER_LOCATION;
  const qty = Number(quantity) || 0;
  const smallQty =
    qty > 0 && qty < EMBROIDERY_SMALL_QTY_THRESHOLD ? EMBROIDERY_SMALL_QTY_FEE : 0;
  return digitizing + smallQty;
}

/**
 * @returns {{
 *   perPiece: number,
 *   baselinePerPiece: number,
 *   baselineCogs: number|null,
 *   setupFee: number,
 *   source: string,
 *   tierMinQty: number|null,
 *   decorationOnly: boolean,
 * } | null}
 */
export function lookupRetailPrice({
  decorationType,
  quantity,
  locationCount,
  locations,
  cogs,
}) {
  if (decorationType === 'embroidery') {
    const bucket = embroideryQtyBucket(quantity);
    if (!bucket) return null;
    if (bucket === 'unavailable') return null;

    const enabled = ['front', 'back', 'other'].filter((key) => locations?.[key]?.enabled);
    if (enabled.length < 1) return null;

    let decoration = 0;
    for (const key of enabled) {
      const tierValue = locations[key]?.stitchTier || 'under5k';
      const locPrice = embroideryDecorationPerLocation(quantity, tierValue);
      if (locPrice == null) return null;
      decoration += locPrice;
    }

    const garmentCost = Number(cogs) || 0;
    // Sheet excludes apparel — all-in ask adds garment COGS
    const perPiece = decoration + garmentCost;

    return {
      perPiece,
      baselinePerPiece: decoration,
      baselineCogs: null,
      setupFee: embroideryDigitizingFee(enabled.length, quantity),
      source: EMBROIDERY_DECORATION.label,
      tierMinQty: bucket === 'small' ? 1 : 25,
      decorationOnly: true,
    };
  }

  const type = decorationType === 'dtf' ? 'dtf' : decorationType === 'screen' ? 'screen' : null;
  if (!type) return null;

  const sheet = RETAIL_BASELINES[type];
  const qty = Number(quantity) || 0;
  if (sheet.maxQty != null && qty > sheet.maxQty) return null;

  const locs = clampLocations(locationCount, sheet.tiers[0].prices.length);
  if (locs < 1) return null;

  const tier = lookupTier(sheet.tiers, qty);
  if (!tier) return null;

  const baselinePerPiece = tier.prices[locs - 1];
  if (baselinePerPiece == null) return null;

  // Sheet price bakes in the example garment — swap it for the actual one
  const decorationPerPiece = baselinePerPiece - sheet.cogs;
  const garmentCost = Number(cogs) || 0;

  return {
    perPiece: decorationPerPiece + garmentCost,
    baselinePerPiece,
    decorationPerPiece,
    baselineCogs: sheet.cogs,
    setupFee: sheet.setupFee || 0,
    source: sheet.label,
    tierMinQty: tier.minQty,
    decorationOnly: false,
  };
}
